import { useState, useEffect } from 'react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Textarea } from '@paulownia/shared/components/ui/textarea';
import { Switch } from '@paulownia/shared/components/ui/switch';
import { Label } from '@paulownia/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@paulownia/shared/components/ui/dialog';
import { Plus, Edit, Trash2, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  listMarkets,
  createMarket,
  updateMarket,
  deleteMarket,
} from '@paulownia/shared/services/markets';
import type { Market, DayOfWeek } from '@paulownia/shared/types';
import { DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';

interface MarketFormData {
  city: string;
  dayOfWeek: DayOfWeek;
  openingTime: string;
  closingTime: string;
  description: string;
  address: string;
  picture: string;
  isActive: boolean;
}

const initialFormData: MarketFormData = {
  city: '',
  dayOfWeek: 1,
  openingTime: '08:00',
  closingTime: '13:00',
  description: '',
  address: '',
  picture: '',
  isActive: true,
};

export const MarketsAdmin = () => {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMarket, setEditingMarket] = useState<Market | null>(null);
  const [formData, setFormData] = useState<MarketFormData>(initialFormData);

  useEffect(() => {
    loadMarkets();
  }, []);

  const loadMarkets = async () => {
    setLoading(true);
    try {
      const data = await listMarkets();
      setMarkets(data);
    } catch (error) {
      console.error('Error loading markets:', error);
      toast.error('Erreur lors du chargement des marchés');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (market?: Market) => {
    if (market) {
      setEditingMarket(market);
      setFormData({
        city: market.city,
        dayOfWeek: market.dayOfWeek,
        openingTime: market.openingTime,
        closingTime: market.closingTime,
        description: market.description || '',
        address: market.address,
        picture: market.picture || '',
        isActive: market.isActive,
      });
    } else {
      setEditingMarket(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMarket(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.city.trim() || !formData.address.trim()) {
      toast.error('La ville et l\'adresse sont obligatoires');
      return;
    }

    setLoading(true);
    try {
      if (editingMarket) {
        await updateMarket(editingMarket.id, {
          city: formData.city.trim(),
          dayOfWeek: formData.dayOfWeek,
          openingTime: formData.openingTime,
          closingTime: formData.closingTime,
          description: formData.description.trim() || undefined,
          address: formData.address.trim(),
          picture: formData.picture.trim() || undefined,
          isActive: formData.isActive,
        });
        toast.success('Marché mis à jour');
      } else {
        await createMarket({
          city: formData.city.trim(),
          dayOfWeek: formData.dayOfWeek,
          openingTime: formData.openingTime,
          closingTime: formData.closingTime,
          description: formData.description.trim() || undefined,
          address: formData.address.trim(),
          picture: formData.picture.trim() || undefined,
          isActive: formData.isActive,
        });
        toast.success('Marché créé');
      }
      handleCloseDialog();
      await loadMarkets();
    } catch (error) {
      console.error('Error saving market:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (market: Market) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le marché "${market.city} - ${DAY_OF_WEEK_LABELS[market.dayOfWeek]}" ?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteMarket(market.id);
      toast.success('Marché supprimé');
      await loadMarkets();
    } catch (error) {
      console.error('Error deleting market:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (market: Market) => {
    setLoading(true);
    try {
      await updateMarket(market.id, { isActive: !market.isActive });
      toast.success(market.isActive ? 'Marché désactivé' : 'Marché activé');
      await loadMarkets();
    } catch (error) {
      console.error('Error toggling market:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Gestion des marchés</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} disabled={loading}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau marché
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMarket ? 'Modifier le marché' : 'Créer un marché'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Ville *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Ex: Lyon"
                    required
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dayOfWeek">Jour *</Label>
                  <Select
                    value={String(formData.dayOfWeek)}
                    onValueChange={(value) =>
                      setFormData({ ...formData, dayOfWeek: Number(value) as DayOfWeek })
                    }
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DAY_OF_WEEK_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="openingTime">Heure d'ouverture *</Label>
                  <Input
                    id="openingTime"
                    type="time"
                    value={formData.openingTime}
                    onChange={(e) => setFormData({ ...formData, openingTime: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="closingTime">Heure de fermeture *</Label>
                  <Input
                    id="closingTime"
                    type="time"
                    value={formData.closingTime}
                    onChange={(e) => setFormData({ ...formData, closingTime: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Adresse exacte *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Ex: Place du Marché, 69001 Lyon"
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du marché..."
                  rows={3}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="picture">URL de la photo</Label>
                <Input
                  id="picture"
                  type="url"
                  value={formData.picture}
                  onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
                  placeholder="https://..."
                  disabled={loading}
                />
                {formData.picture && (
                  <div className="mt-2">
                    <img
                      src={formData.picture}
                      alt="Aperçu"
                      className="w-full h-32 object-cover rounded-md"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked: boolean) => setFormData({ ...formData, isActive: checked })}
                  disabled={loading}
                />
                <Label htmlFor="isActive">Marché actif</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={loading}>
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Enregistrement...' : editingMarket ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Markets List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {markets.length === 0 ? (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">
                Aucun marché créé. Cliquez sur "Nouveau marché" pour commencer.
              </p>
            </CardContent>
          </Card>
        ) : (
          markets.map((market) => (
            <Card
              key={market.id}
              className={`${!market.isActive ? 'opacity-60' : ''}`}
            >
              {market.picture && (
                <div className="relative h-32 overflow-hidden rounded-t-lg">
                  <img
                    src={market.picture}
                    alt={market.city}
                    className="w-full h-full object-cover"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.parentElement!.style.display = 'none';
                    }}
                  />
                  {!market.isActive && (
                    <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
                      <span className="text-white font-medium">Inactif</span>
                    </div>
                  )}
                </div>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <span className="text-lg">{market.city}</span>
                  <span className="text-sm font-normal text-muted-foreground">
                    {DAY_OF_WEEK_LABELS[market.dayOfWeek]}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{market.openingTime} - {market.closingTime}</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{market.address}</span>
                </div>
                {market.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {market.description}
                  </p>
                )}

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={market.isActive}
                      onCheckedChange={() => handleToggleActive(market)}
                      disabled={loading}
                    />
                    <span className="text-sm text-muted-foreground">
                      {market.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(market)}
                      disabled={loading}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(market)}
                      disabled={loading}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
